import { useCallback, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import MenteeHeader from "../../components/MenteeHeader";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PurchaseModal from "./PurchaseModal";
import BackButton from "../../components/BackButton";
import { baseFetch } from "../../helpers/apiClient";
import Background from "../../components/Background";
import SuccessModal from "../../components/SuccessModal";
import { formatEventType } from "../../constants/RegionalLocationList";
import { getTimezoneLabel } from "../../helpers/constants";
import { formatPrice, discountedEventPrice } from "../../helpers/currency";
import DashboardFooter from "../../components/Footer";
import Loader from "../../components/Loader";
import { toggleSessionModal } from "../../redux/sessionSlice";
import PlayIcon from "../../assets/images/play-icon.png";
import VideoPlayer from "../../components/VideoPlayer";
import { styles } from "./style";
const EventDetails = () => {
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const isPremium = useSelector(
    (state) => state.profileSlice?.profile?.account?.isPremiumUser,
  );
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const myEvents = queryParams.get("my-events");
  const [searchTerm, setSearchTerm] = useState("");
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    if (role === "sponsor") {
      return "managecontent";
    }
    return myEvents ? "myevents" : "learn";
  });
  const [open, setOpen] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [eventData, setEventData] = useState({});
  const dispatch = useDispatch();
  // Prefer the backend's authoritative fileType; fall back to the URL extension
  // (stripping any query string so signed S3 URLs like `…/file.pdf?X-Amz-…`
  // don't get misread as non-PDF).
  const urlExtension = eventData?.resourceFile
    ? eventData.resourceFile.split("?")[0].split(".").pop().toLowerCase()
    : "";
  const isPdf =
    eventData?.resourceFileType?.toLowerCase().includes("pdf") ||
    urlExtension === "pdf";
  const userId = localStorage.getItem("userId");
  const params = useParams();
  const eventRequest = useRef(null);
  const fetchEvent = useCallback(() => {
    eventRequest.current?.abort();
    const controller = new AbortController();
    eventRequest.current = controller;
    setLoading(true);
    baseFetch({
      url: `/v1/web/event/single/${params.id}`,
      method: "GET",
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        setEventData({
          _id: response?.data?.event?.id ?? response?.data?.event?._id,
          eventTitle: response?.data?.event?.eventTitle,
          eventType: response?.data?.event?.eventType,
          eventFee: response?.data?.event?.eventFee,
          discountInfo: { ...response?.data?.event?.discountInfo },
          eventDateStart: response?.data?.event?.eventDateStart,
          eventDateEnd: response?.data?.event?.eventDateEnd,
          eventTimeStart: response?.data?.event?.eventTimeStart,
          eventTimeEnd: response?.data?.event?.eventTimeEnd,
          timezone: response?.data?.event?.timezone,
          eventUrl: response?.data?.event?.eventUrl,
          numberOfParticipants: response?.data?.event?.numberOfParticipants,
          eventDescription: response?.data?.event?.eventDescription,
          photo: response?.data?.event?.eventPhoto,
          resourceFile: response?.data?.event?.resourceFile?.url,
          resourceFileName: response?.data?.event?.resourceFile?.fileName,
          resourceFileType: response?.data?.event?.resourceFile?.fileType,
          createdBy: response?.data?.event?.createdBy,
          isAlreadyAdded: response?.data?.event?.isAlreadyAdded,
          isUpdated: response?.data?.event?.isUpdated,
          isPurchased: response?.data?.event?.isPurchased,
        });
        setLoading(false);
        setOpen(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.log("err", err);
        setLoading(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  }, [params.id, dispatch]);

  useEffect(() => {
    fetchEvent();
    return () => eventRequest.current?.abort();
  }, [fetchEvent]);
  const handleChangeActive = (param) => {
    setActiveTab(param);
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const isOwnEvent =
    role === "sponsor" && eventData?.createdBy?.userId === userId;
  // Paid resource is accessible without (further) purchase: free, premium,
  // already purchased, or the sponsor's own event.
  const isUnlocked =
    !eventData?.eventFee ||
    eventData?.eventFee <= 0 ||
    isPremium ||
    eventData?.isPurchased ||
    isOwnEvent;
  const showButton =
    eventData?.eventType === "webinar" ||
    eventData?.eventType === "virtual-event" ||
    (eventData?.eventType === "videos-and-presentations" &&
      eventData?.eventFee > 0);
  const handleJoining = () => {
    window.open(eventData?.eventUrl, "_blank");
  };

  const handleEventRegistration = () => {
    setError("");
    const payload = {
      eventId: params.id,
    };
    if (!isPremium && eventData?.eventFee > 0) {
      setOpen(true);
    } else {
      setLoading(true);
      baseFetch({
        url: "/v1/web/event/register/workshop",
        method: "POST",
        body: payload,
      })
        .then((response) => {
          if (response.status) {
            setLoading(false);
            setOpenSuccessModal(true);
          }
        })
        .catch((err) => {
          setLoading(false);
          console.log("error is", err);
          setError(err);
        });
    }
  };

  const getEventTypeLabel = () => {
    switch (eventData?.eventType) {
      case "webinar":
        return "Webinar";
      case "virtual-event":
        return "Event";
      case "workshop":
        return "Workshop";
      default:
        return "Event";
    }
  };

  const getViewButton = () => {
    // An article/PDF entry can reach here with neither a file nor a link (e.g. the
    // resource was never uploaded). Show a disabled affordance with a warning rather
    // than an active button that would open a blank tab.
    const hasViewableContent = Boolean(
      eventData?.resourceFile || eventData?.eventUrl,
    );
    if (!hasViewableContent) {
      return (
        <Box className="pt-3">
          <Button
            disabled
            startIcon={<WarningAmberRoundedIcon />}
            sx={styles.ctaButtonDisabled}
          >
            Content Unavailable
          </Button>
        </Box>
      );
    }
    return (
      <Box className="pt-3">
        <Button
          sx={styles.ctaButtonPrimaryWhite}
          onClick={() => {
            if (eventData?.resourceFile) {
              navigate("/event-details/view-pdf", {
                state: {
                  resourceFileUrl: eventData?.resourceFile,
                  resourceFileName: eventData?.resourceFileName,
                  title: eventData?.eventTitle,
                },
              });
            } else {
              window.open(eventData.eventUrl, "_blank", "noopener,noreferrer");
            }
          }}
        >
          View
        </Button>
      </Box>
    );
  };

  const getCTAButton = () => {
    const label = getEventTypeLabel();
    const isPast = moment
      .utc()
      .isAfter(
        moment.utc(eventData?.eventDateEnd || eventData?.eventDateStart),
      );
    if (isPast) {
      return (
        <Button disabled={true} sx={styles.ctaButtonDisabled}>
          {label} Ended
        </Button>
      );
    } else if (eventData?.isPurchased) {
      return (
        <Button
          onClick={() => window.open(eventData?.eventUrl, "_blank")}
          sx={styles.ctaButtonJoin}
        >
          Join {label}
        </Button>
      );
    } else {
      return (
        <>
          <Button
            onClick={myEvents ? handleJoining : handleEventRegistration}
            sx={styles.ctaButtonPrimary}
          >
            {myEvents ? `Join ${label}` : `Register ${label}`}
          </Button>
          {error && <p style={{ color: "red" }}>{error}</p>}
        </>
      );
    }
  };

  return (
    <>
      <Background>
        {loading && <Loader />}
        <MenteeHeader
          role={role}
          active={activeTab}
          searchTerm={searchTerm}
          handleChangeActive={handleChangeActive}
          handleSearchChange={handleSearchChange}
        />
        <Box sx={styles.pageWrapper} className="dashboard-main-div">
          <BackButton
            leftNormal={true}
            onBackStep={() => {
              if (role !== "sponsor") {
                myEvents
                  ? navigate("/dashboard?active=myevents")
                  : navigate("/dashboard");
              } else {
                navigate("/dashboard?active=managecontent", {
                  state: location?.state?.filter,
                });
              }
            }}
          />
          <Box sx={styles.contentPadding}>
            <Container maxWidth="xl">
              <Grid container spacing={{ lg: 3, sm: 2, xs: 2 }}>
                <Grid item xs={12} lg={8} xl={8} sx={styles.gridItem}>
                  <Box sx={styles.card}>
                    <Box sx={styles.leftColumn}>
                      <Box sx={styles.imageContainer}>
                        <Box sx={styles.eventTypeBadge}>
                          {formatEventType("key", eventData?.eventType)}
                        </Box>
                        <Box sx={styles.imageOverlay}></Box>
                        <Box sx={styles.imageWrapper}>
                          {eventData?.photo && !loading && (
                            <Box
                              component="img"
                              src={eventData?.photo}
                              alt="logo"
                              sx={styles.eventImage}
                            />
                          )}
                          {eventData?.eventType ===
                            "videos-and-presentations" &&
                            !isPdf &&
                            isUnlocked && (
                              <Box
                                sx={styles.playButton}
                                onClick={() => setOpenVideoModal(true)}
                              >
                                <img src={PlayIcon} width={40} />
                              </Box>
                            )}
                        </Box>
                      </Box>
                      <Box sx={styles.detailsSection}>
                        <Box sx={{ textAlign: "left" }}>
                          <Typography sx={styles.eventTitle} varient="h5">
                            {eventData?.eventTitle}
                          </Typography>
                          {role === "sponsor" &&
                            eventData?.eventType !=
                              "videos-and-presentations" && (
                              <Box className="mt-2">
                                <Typography sx={styles.sectionLabel}>
                                  Event URL
                                </Typography>
                                <Box sx={{ marginTop: "10px" }}>
                                  <Typography sx={styles.eventUrlText}>
                                    {eventData?.eventUrl}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          {showButton && !isOwnEvent && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {/* {role !== 'sponsor' && isPremium && (
                                <Typography
                                  sx={{
                                    textDecoration: 'line-through',
                                    color: '#2A4876',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    marginBottom: '4px',
                                    marginTop: '5px',
                                  }}
                                >
                                  ${eventData?.eventFee}
                                </Typography>
                              )}

                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#2A4876',
                                  padding: '5px 10px',
                                  borderRadius: '10px',
                                  marginBottom: '10px',
                                  marginTop: '10px',
                                  marginLeft: role !== 'sponsor' && isPremium ? '5px' : '0px',
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#fff',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                  }}
                                  variant="span"
                                >
                                  {role !== 'sponsor' && isPremium
                                    ? '$0'
                                    : `$${eventData?.eventFee}`}
                                </Typography>
                              </Box> */}
                              {/* {role !== 'sponsor' && ( */}
                              <Typography
                                sx={styles.priceOriginal(
                                  !!(
                                    isPremium ||
                                    eventData?.discountInfo?.hasDiscount ||
                                    role === "sponsor"
                                  ) && !!eventData?.eventFee,
                                )}
                              >
                                ${formatPrice(eventData?.eventFee)}
                              </Typography>
                              {/* )} */}
                              {eventData?.eventFee > 0 &&
                                (isPremium ||
                                  eventData?.discountInfo?.hasDiscount) && (
                                  <Box
                                    sx={styles.priceDiscountBadge(
                                      role === "sponsor",
                                    )}
                                  >
                                    <Typography
                                      sx={styles.priceDiscountText}
                                      variant="span"
                                    >
                                      {role !== "sponsor"
                                        ? isPremium
                                          ? "$0"
                                          : eventData?.discountInfo?.hasDiscount
                                            ? `$${formatPrice(discountedEventPrice(eventData))}`
                                            : `$${formatPrice(eventData?.eventFee)}`
                                        : `$${formatPrice(eventData?.eventFee)}`}
                                    </Typography>
                                  </Box>
                                )}
                            </Box>
                          )}
                        </Box>
                        {showButton && (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                paddingBottom: "10px",
                                flexDirection: "column",
                              }}
                            >
                              <Box sx={styles.timeRow}>
                                <Box
                                  component="img"
                                  src="/images/icons/time-circle-icon-black.png"
                                  alt="logo"
                                  sx={styles.icon}
                                />
                                <Typography sx={styles.metaText}>
                                  {eventData?.eventTimeStart}-
                                  {eventData?.eventTimeEnd}{" "}
                                  {getTimezoneLabel(eventData?.timezone)}
                                </Typography>
                              </Box>
                              <Box sx={styles.dateRow}>
                                <Box
                                  component="img"
                                  src="/images/icons/calendar-icon-black.png"
                                  alt="logo"
                                  sx={styles.icon}
                                />
                                <Typography sx={styles.metaText}>
                                  {eventData?.eventDateStart
                                    ? new Date(
                                        eventData.eventDateStart,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "2-digit",
                                        year: "numeric",
                                        timeZone: eventData?.timezone || "UTC",
                                      })
                                    : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </>
                        )}
                        {role !== "sponsor" && (
                          <Box sx={styles.sponsorRow}>
                            {eventData?.createdBy?.sponsorProfile?.logo && (
                              <Box
                                component="img"
                                src={eventData?.createdBy?.sponsorProfile?.logo}
                                alt="logo"
                                sx={styles.sponsorLogo}
                              />
                            )}
                            {eventData?.createdBy?.sponsorProfile
                              ?.companyName && (
                              <Typography sx={styles.sponsorName}>
                                {
                                  eventData?.createdBy?.sponsorProfile
                                    ?.companyName
                                }
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box sx={styles.rightColumn}>
                      <Typography sx={styles.sectionLabel}>
                        Description
                      </Typography>
                      <Box
                        className="overflow-scroll-none"
                        sx={styles.descriptionContainer}
                      >
                        {/* eventDescription is operator/sponsor-authored rich text
                            (Quill HTML); sanitize before rendering to prevent stored XSS. */}
                        <Box
                          sx={styles.descriptionContent}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              eventData.eventDescription || "",
                            ),
                          }}
                        ></Box>
                      </Box>
                      {role === "sponsor" && (
                        <>
                          {eventData.numberOfParticipants > 0 && (
                            <>
                              <Typography sx={styles.sectionLabel}>
                                Number of Participants
                              </Typography>

                              <Box sx={{ marginTop: "10px" }}>
                                <Typography sx={styles.participantCount}>
                                  {eventData?.numberOfParticipants}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </>
                      )}
                      {showButton &&
                        role !== "sponsor" &&
                        ![
                          "videos-and-presentations",
                          "articles-and-blogs",
                        ].includes(eventData?.eventType) && (
                          <Box>{getCTAButton()}</Box>
                        )}
                      {eventData?.eventType === "videos-and-presentations" &&
                      isPdf ? (
                        isUnlocked ? (
                          getViewButton()
                        ) : (
                          <Box>
                            <Button
                              onClick={handleEventRegistration}
                              sx={styles.ctaButtonPrimary}
                            >
                              Purchase Presentation
                            </Button>
                            {error && <p style={{ color: "red" }}>{error}</p>}
                          </Box>
                        )
                      ) : eventData?.eventType === "articles-and-blogs" ||
                        isPdf ? (
                        getViewButton()
                      ) : eventData?.eventType === "videos-and-presentations" &&
                        !isPdf ? (
                        isUnlocked ? (
                          <Box>
                            <Button
                              className="mb-1"
                              sx={styles.ctaButtonPrimaryWhite}
                              onClick={() => setOpenVideoModal(true)}
                            >
                              Watch Video
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            <Button
                              onClick={handleEventRegistration}
                              sx={styles.ctaButtonPrimary}
                            >
                              Purchase Video
                            </Button>
                            {error && <p style={{ color: "red" }}>{error}</p>}
                          </Box>
                        )
                      ) : (
                        ""
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Box>
        <DashboardFooter />
        {open && (
          <PurchaseModal
            success={() => {
              setOpen(false);
              setShowSuccess(true);
              fetchEvent();
            }}
            handleLoading={(val) => setLoading(val)}
            event={eventData}
            open={open}
            onClose={() => setOpen(false)}
          />
        )}
        {showSuccess && (
          <SuccessModal
            open={showSuccess}
            onClose={() => {
              setShowSuccess(false);
              navigate("/dashboard?active=myevents");
            }}
            title={"Congratulations on your purchase!"}
            subTitle={`Get ready to enhance your skills and advance your career. Happy
            Learning!`}
          />
        )}
        {openSuccessModal && (
          <SuccessModal
            open={openSuccessModal}
            onClose={() => {
              setOpenSuccessModal(false);
              navigate("/dashboard?active=myevents");
            }}
            title="Event Registration Successful"
            subTitle="Your Event has been Registered Successfully"
          />
        )}
        {openVideoModal && (
          <VideoPlayer
            openVideoModal={openVideoModal}
            handleClose={() => setOpenVideoModal(false)}
            videoUrl={eventData?.resourceFile}
          />
        )}
      </Background>
    </>
  );
};

export default EventDetails;
